/**
 * @fileoverview Error Context
 * @description Provides centralized error handling for API errors across the application.
 *              Distinguishes between authentication errors (401) and authorization errors (403).
 *
 * @author Sabya (Sabyasachi Rout)
 * @version 1.1.0
 *
 * @changelog
 * - 2024-12-04: Updated handleAPIError to distinguish 401 (auth) vs 403 (permission) errors (Sabya)
 * - 2024-12-04: 401 errors now trigger logout, 403 errors show toast message (Sabya)
 */

import { useNavigate } from "react-router-dom";
import { createContext, useContext } from "react";

import { useAuth } from "./auth.context";

import { showErrorToast } from "../utils/error.util";

export const ErrorContext = createContext(null);

/**
 * Error Context Provider Component
 * Provides error handling utilities to child components
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export function ErrorContextProvider({ children }) {
  const navigate = useNavigate();
  const { logOut } = useAuth();

  /**
   * Handles API errors with appropriate user feedback
   *
   * Error handling strategy:
   * - 401 (Unauthorized): Session expired or invalid token → Logout and redirect to login
   * - 403 (Forbidden): User lacks permission for action → Show error toast
   * - Other errors: General API failures → Show error toast
   *
   * @param {Object} error - Axios error object
   * @param {Object} [error.response] - HTTP response object (if available)
   * @param {number} [error.response.status] - HTTP status code
   * @param {Object} [error.response.data] - Response body with error details
   * @param {string} [error.message] - Error message for network failures
   */
  function handleAPIError(error) {
    console.log(error);

    if (error.response) {
      if (error.response.status === 401) {
        // 401 = Authentication error (invalid/expired token) - log out
        logOut();
        navigate("/login", { replace: true });
      } else {
        // 403 and other errors - show toast (includes authorization errors like "not allowed to delete")
        showErrorToast(
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.response?.data?.detail?.[0]?.msg ||
          "Something went wrong"
        );
      }
    } else {
      showErrorToast(error.message || "Something went wrong");
    }
  }

  return (
    <ErrorContext.Provider value={{ handleAPIError }}>
      {children}
    </ErrorContext.Provider>
  );
}

export const useError = () => useContext(ErrorContext);
